/**
 * Orders Page for Admin PWA
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Filter,
  Clock,
  DollarSign,
  User,
  ChevronRight
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useBranch } from '../context/BranchContext';
import { accountHeaders } from '@/lib/pwaAccount';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { getBranchHeaders } = useBranch();


  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/orders?status=${filter}`, {
        headers: {
          ...accountHeaders(),
          ...getBranchHeaders()
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || data || []);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, getBranchHeaders]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const statusColors = {
    pending: 'bg-amber-500/20 text-amber-400',
    confirmed: 'bg-blue-500/20 text-blue-400',
    preparing: 'bg-purple-500/20 text-purple-400',
    ready: 'bg-emerald-500/20 text-emerald-400',
    completed: 'bg-slate-500/20 text-slate-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };

  const filters = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Orders</h1>
          <p className="text-slate-400 text-sm mt-0.5 sm:mt-1">
            View and manage orders
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
        {filters.map(f => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className={`h-8 text-xs flex-shrink-0 ${filter === f.value ? 'bg-emerald-600' : 'border-slate-700'}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Orders list */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-20 bg-slate-900 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : orders.length > 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <div className="divide-y divide-slate-800">
            {orders.map(order => (
              <div key={order.id} className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 hover:bg-slate-800/50 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <span className="font-mono text-white text-xs sm:text-sm">#{order.order_number || String(order.id || '').slice(-6)}</span>
                    <Badge className={`text-[10px] sm:text-xs ${statusColors[order.status?.toLowerCase()] || 'bg-slate-500/20'}`}>
                      {order.status || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 mt-1 text-[10px] sm:text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      {new Date(order.created_at).toLocaleTimeString()}
                    </span>
                    {order.customer_name && (
                      <span className="flex items-center gap-1 truncate">
                        <User className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="truncate">{order.customer_name}</span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <p className="text-emerald-400 font-medium text-xs sm:text-sm">
                    ${((order.total || 0) / 100).toFixed(2)}
                  </p>
                  <p className="text-[10px] sm:text-sm text-slate-500">
                    {order.items?.length || 0} items
                  </p>
                </div>

                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 flex-shrink-0 hidden xs:block" />
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="flex flex-col items-center justify-center py-10 sm:py-16">
            <p className="text-slate-400 text-sm">No orders found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
